import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { buildBusinessUrl } from '../lib/businessUrl';
import { recommendationsApi } from '../services/api';
import { RecommendationSection, RecommendationSkeleton } from '../components/RecommendationSection';
import DiscoveryCarousel from '../components/DiscoveryCarousel';
import { getCategoryIcon } from '../lib/categoryIcons';
import { toast } from 'sonner';
import { 
  MapPin, 
  Phone, 
  MessageCircle, 
  Instagram, 
  Clock,
  ArrowLeft,
  Navigation,
  CheckCircle,
  Edit2,
  Star,
  Send,
  Globe,
  Mail,
  CreditCard,
  Wifi,
  Car,
  Baby,
  Dog,
  Snowflake,
  TreeDeciduous,
  ShieldCheck,
  Accessibility,
  Calendar,
  AlertCircle,
  Briefcase,
  Heart,
  Leaf,
  ShoppingBag,
  Users,
  Award,
  ExternalLink,
  Building,
  Eye,
  Share2,
  Copy,
  Check,
  Flag,
  X,
  Loader2,
  Tag,
  Flame,
  Megaphone,
  Sparkles,
  Handshake,
  Plus,
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/StarRating';
import ImageGallery from '../components/ImageGallery';
import FavoriteButton from '../components/FavoriteButton';
import ShareButton from '../components/ShareButton';
import CommentSection from '../components/CommentSection';
import Breadcrumbs from '../components/Breadcrumbs';
import SEOHead, { buildLocalBusinessJsonLd, buildBreadcrumbJsonLd } from '../components/SEOHead';
import BusinessTagsChips from '../components/BusinessTagsChips';
import FeedCard from '../components/FeedCard';
// Use centralized API service
import { 
  businessesApi,
  municipiosApi,
  reviewsApi,
  api,
  analyticsApi,
  jobsApi,
  reportsApi
} from '../services/api';
import { BusinessBadgesInline, BusinessLevelBadge } from '../components/BusinessBadges';
import { openWhatsApp } from '../utils/whatsappLink';
import { resolveBackendImage } from '../utils/brandLogo';
import MeuNegocioLiteModal from '../components/MeuNegocioLiteModal';
import AuthModal from '../components/AuthModal';
import useMunicipioFeatures from '../hooks/useMunicipioFeatures';

const API = process.env.REACT_APP_BACKEND_URL;

// Motivos de denúncia
const REPORT_REASONS = [
  { value: 'informacoes_falsas', label: 'Informações falsas ou enganosas' },
  { value: 'conteudo_inadequado', label: 'Conteúdo inadequado ou ofensivo' },
  { value: 'negocio_fechado', label: 'Negócio fechado ou inexistente' },
  { value: 'spam', label: 'Spam ou propaganda enganosa' },
  { value: 'outro', label: 'Outro motivo' }
];

// Badge component for features
const FeatureBadge = ({ icon: Icon, label, color = "gray" }) => {
  const colorClasses = {
    gray: "bg-gray-100 text-gray-700",
    green: "bg-green-100 text-green-700",
    blue: "bg-blue-100 text-blue-700",
    purple: "bg-purple-100 text-purple-700",
    orange: "bg-orange-100 text-orange-700",
    teal: "bg-teal-100 text-teal-700",
    pink: "bg-pink-100 text-pink-700",
    indigo: "bg-indigo-100 text-indigo-700"
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${colorClasses[color]}`}>
      {Icon && <Icon className="w-4 h-4" />}
      {label}
    </span>
  );
};

// Section component
const Section = ({ title, icon: Icon, children, className = "" }) => (
  <div className={`p-6 border-b border-[var(--border)] ${className}`}>
    <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
      {Icon && <Icon className="w-5 h-5 text-[var(--primary)]" />}
      {title}
    </h2>
    {children}
  </div>
);

export default function NegocioDetalhes() {
  const params = useParams();
  // Three URL formats:
  //  1. NEW SEO:  /:municipioSlug/:categoriaSlug/:businessSlug   (item #11)
  //               The router param can be `businessSlug` (legacy) or `secondSlug`
  //               (new dual-purpose route — see CategoryOrBusinessRouter).
  //  2. LEGACY:   /negocio/:id  (or /negocio/:businessSlug with `slug--id`)
  //  3. TENANT-LEGACY: /:municipioSlug/negocio/:id
  const seoBusinessSlug = params.businessSlug || params.secondSlug || null;
  const usingSeoPath = !!(params.categoriaSlug && seoBusinessSlug);
  let id = params.id || seoBusinessSlug || null;
  if (id && id.includes('--')) {
    // SEO slug format used historically: "slug--id"
    id = id.split('--').pop();
  }
  const municipioSlug = params.municipioSlug;
  const categoriaSlug = params.categoriaSlug;
  const businessSlugFromPath = seoBusinessSlug;
  const navigate = useNavigate();
  const { location } = useLocation();
  const { user, token } = useAuth();
  
  const [business, setBusiness] = useState(null);
  const { delivery_enabled: municipioDeliveryEnabled } = useMunicipioFeatures(business?.municipio_slug || null);
  // Modal de login inline — evita navegar para /perfil quando o usuário
  // deslogado tenta avaliar/comentar/denunciar. Assim ele mantém o contexto
  // do negócio e completa a ação imediatamente após logar.
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [sharingBiz, setSharingBiz] = useState(false);
  const [copiedBiz, setCopiedBiz] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [ratingStats, setRatingStats] = useState(null);
  const [similarBusinesses, setSimilarBusinesses] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [recsLoading, setRecsLoading] = useState(false);
  // Sprint 1: novo sistema de descoberta (3 carrosséis unificados)
  const [descoberta, setDescoberta] = useState(null);
  const [descobertaLoading, setDescobertaLoading] = useState(false);
  const [feedPosts, setFeedPosts] = useState([]);
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedCategoriasMap, setFeedCategoriasMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [municipio, setMunicipio] = useState(null);
  
  // Review form
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    comment: ''
  });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Report (Denúncia)
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportForm, setReportForm] = useState({
    reason: '',
    description: ''
  });
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);

  // Claim (Reivindicação)
  const [managementInfo, setManagementInfo] = useState(null);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [claimStatus, setClaimStatus] = useState(null);
  const [claimCode, setClaimCode] = useState('');
  const [submittingClaim, setSubmittingClaim] = useState(false);
  const [relatedContent, setRelatedContent] = useState(null);
  
  // Document upload for dispute
  const [documentFile, setDocumentFile] = useState(null);
  const [documentType, setDocumentType] = useState('CNPJ');
  const [documentDescription, setDocumentDescription] = useState('');

  // Jobs (Vagas)
  const [jobs, setJobs] = useState([]);

  // Promotions
  const [promotions, setPromotions] = useState([]);
  const [campanhasParticipando, setCampanhasParticipando] = useState([]);
  const [coletivosParticipando, setColetivosParticipando] = useState([]);
  const [showLiteEditor, setShowLiteEditor] = useState(false);
  const [searchParams] = useSearchParams();
  const cupomParam = searchParams.get('cupom');
  const promosRef = React.useRef(null);

  useEffect(() => {
    // Item #13: scroll user to top when arriving on a business page (cards
    // sometimes navigate from a deep scroll position which dropped the user
    // mid-page on the new route).
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    loadBusiness();
    loadReviews();
    loadRatingStats();
    trackView();
    loadManagementInfo();
    loadJobs();
    loadMunicipio();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location]);

  // Load promotions for this business — filtra apenas ativos
  // Usa business.id (resolvido após loadBusiness) pois `id` pode ser um slug
  const loadPromotions = async (bizId) => {
    const target = bizId || business?.id;
    if (!target) return;
    try {
      const res = await api.get(`/v2/coupons/business/${target}`, { params: { ativo: 'true' } });
      setPromotions(res.data || []);
    } catch { setPromotions([]); }
  };

  // Recarrega promotions quando business resolver
  useEffect(() => {
    if (business?.id) loadPromotions(business.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  // Quando o negócio for finalmente resolvido a partir de um slug SEO,
  // reexecutamos as leituras dependentes do id real para garantir que
  // reviews, stats, jobs e info de gestão correspondam ao negócio carregado.
  useEffect(() => {
    if (!business?.id) return;
    if (business.id === id) return; // já foi carregado com id real
    loadReviews();
    loadRatingStats();
    loadJobs();
    loadManagementInfo();
    trackView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  // Carrega campanhas em que o negócio está participando (adesões aprovadas)
  useEffect(() => {
    if (!business?.id) {
      setCampanhasParticipando([]);
      setColetivosParticipando([]);
      return;
    }
    api.get(`/v2/campanhas/by-business/${business.id}`, { params: { only_active: true } })
      .then((r) => setCampanhasParticipando(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCampanhasParticipando([]));
    api.get(`/v2/coletivos/by-business/${business.id}`)
      .then((r) => setColetivosParticipando(Array.isArray(r.data) ? r.data : []))
      .catch(() => setColetivosParticipando([]));
  }, [business?.id]);

  // Auto-scroll to promotions section when ?cupom= is in URL
  useEffect(() => {
    if (cupomParam && promotions.length > 0 && promosRef.current) {
      setTimeout(() => {
        promosRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 500);
    }
  }, [cupomParam, promotions]);

  // Load municipality info - from URL or from business data
  const loadMunicipio = async () => {
    try {
      const allMunicipios = await municipiosApi.list();
      if (municipioSlug) {
        const found = allMunicipios.find(m => m.slug === municipioSlug);
        if (found) setMunicipio(found);
      } else if (business?.municipio_id) {
        // If no slug in URL but business has municipio_id, use that
        const found = allMunicipios.find(m => m.id === business.municipio_id);
        if (found) setMunicipio(found);
      }
    } catch (e) {
      console.error('Error loading municipio:', e);
    }
  };

  // Reload municipio when business data is available
  useEffect(() => {
    if (business && !municipio) {
      loadMunicipio();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business]);

  // Load similar businesses when business data is available
  useEffect(() => {
    if (business?.categoria) {
      loadSimilarBusinesses();
      loadRelatedContent();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.categoria]);

  // Load recommendations + register visit (co-visit tracking)
  useEffect(() => {
    if (!business?.id) return;
    const sid = (() => {
      let s = localStorage.getItem('session_id');
      if (!s) {
        s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
        localStorage.setItem('session_id', s);
      }
      return s;
    })();
    // Fire-and-forget visit tracking (co-visits)
    recommendationsApi.trackVisit(business.id, sid, business.municipio_id);
    // Load recommendations (legacy — used elsewhere, kept silently)
    setRecsLoading(true);
    recommendationsApi.get(business.id, business.municipio_id)
      .then(setRecommendations)
      .catch(() => setRecommendations(null))
      .finally(() => setRecsLoading(false));
    // Load unified discovery endpoint (Sprint 1: similares + recomendados + em_alta)
    setDescobertaLoading(true);
    recommendationsApi.getDescoberta(business.id, business.municipio_id)
      .then(setDescoberta)
      .catch(() => setDescoberta(null))
      .finally(() => setDescobertaLoading(false));
    // Load feed posts for this business
    setFeedLoading(true);
    api.get(`/v2/feed/negocio/${business.id}`, { params: { limit: 6 } })
      .then(res => setFeedPosts(Array.isArray(res.data) ? res.data : []))
      .catch(() => setFeedPosts([]))
      .finally(() => setFeedLoading(false));
    // Load feed categorias labels (única vez que fizermos essa call por vida do componente)
    if (Object.keys(feedCategoriasMap).length === 0) {
      api.get('/v2/feed/categorias')
        .then((r) => {
          const m = {};
          (r.data?.categorias || []).forEach((c) => { m[c.key] = c.label; });
          setFeedCategoriasMap(m);
        })
        .catch(() => setFeedCategoriasMap({}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const trackView = async () => {
    try {
      // Usa business.id real (não slug SEO) para os contadores não duplicarem entre URLs.
      await analyticsApi.trackView(business?.id || id);
    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
    }
  };

  const loadBusiness = async () => {
    try {
      setLoading(true);
      const params = {};
      if (location) {
        params.lat = location.latitude;
        params.lon = location.longitude;
      }

      let data;
      if (usingSeoPath) {
        // SEO path → resolve via the new endpoint.
        const res = await api.get(
          `/businesses/by-path/${municipioSlug}/${categoriaSlug}/${businessSlugFromPath}`,
          { params },
        );
        data = res.data;
      } else {
        data = await businessesApi.get(id, municipioSlug);
      }
      setBusiness(data);

      // Legacy URL → redirect to canonical SEO URL (item #11 — acts as 301 from the user's perspective).
      if (!usingSeoPath && data?.slug && data?.municipio_slug && data?.categoria_slug) {
        const canonical = buildBusinessUrl(data);
        const search = window.location.search || '';
        const hash = window.location.hash || '';
        if (window.location.pathname !== canonical) {
          navigate(`${canonical}${search}${hash}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Erro ao carregar negócio:', error);
      setError('Negócio não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const loadSimilarBusinesses = async () => {
    try {
      const params = { limit: 10 };
      if (location) {
        params.lat = location.latitude;
        params.lon = location.longitude;
      }
      if (municipioSlug) {
        params.municipio_slug = municipioSlug;
      }
      const data = await businessesApi.list(params);
      // Filter by same category and exclude current business
      const similar = data.filter(b => 
        b.categoria === business.categoria && b.id !== id
      ).slice(0, 6);
      setSimilarBusinesses(similar);
    } catch (error) {
      console.error('Erro ao carregar negócios similares:', error);
    }
  };

  const loadReviews = async () => {
    try {
      // Quando vindo de URL SEO, `id` pode ser slug. Após business carregar,
      // usamos o id real. O useEffect abaixo refaz a chamada quando business?.id muda.
      const data = await reviewsApi.list(business?.id || id);
      setReviews(data);
    } catch (error) {
      console.error('Erro ao carregar avaliações:', error);
    }
  };

  const loadRelatedContent = async () => {
    if (!business) return;
    try {
      const res = await fetch(
        `${API}/api/v2/seo/related?business_id=${business.id}&municipio_slug=${municipioSlug || ''}&categoria_slug=${business.categoria || ''}`
      );
      if (res.ok) setRelatedContent(await res.json());
    } catch {}
  };

  const loadRatingStats = async () => {
    try {
      const data = await reviewsApi.stats(business?.id || id);
      setRatingStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const loadManagementInfo = async () => {
    try {
      const data = await businessesApi.managementInfo(business?.id || id);
      setManagementInfo(data);
    } catch (error) {
      console.error('Erro ao carregar info de gestão:', error);
    }
  };

  const loadJobs = async () => {
    try {
      const data = await jobsApi.getByBusiness(business?.id || id);
      setJobs(data);
    } catch (error) {
      console.error('Erro ao carregar vagas:', error);
    }
  };

  const handleStartClaim = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setShowClaimModal(true);
  };

  const handleSubmitClaim = async () => {
    if (!user || !token) return;
    
    setSubmittingClaim(true);
    try {
      const data = await businessesApi.claim(business?.id || id, { verification_method: 'WHATSAPP' });
      setClaimStatus(data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao iniciar reivindicação');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const handleVerifyClaim = async () => {
    if (!claimStatus?.claim_id || !claimCode) return;
    
    setSubmittingClaim(true);
    try {
      const data = await businessesApi.claimVerify(business?.id || id, claimStatus.claim_id, claimCode);
      alert(data.message);
      setShowClaimModal(false);
      setClaimStatus(null);
      setClaimCode('');
      loadManagementInfo();
    } catch (error) {
      alert(error.response?.data?.detail || 'Código inválido');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const handleDocumentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Max 10MB
      if (file.size > 10 * 1024 * 1024) {
        alert('Arquivo muito grande. Máximo: 10MB');
        return;
      }
      setDocumentFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!documentFile || !claimStatus?.claim_id) return;
    
    setSubmittingClaim(true);
    try {
      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(documentFile);
      });
      
      const { data } = await api.post(
        `/businesses/${id}/claim/${claimStatus.claim_id}/upload-document`,
        {
          document_type: documentType,
          document_description: documentDescription,
          document_base64: base64
        }
      );
      
      alert(data.message);
      setClaimStatus({ ...claimStatus, status: 'DISPUTE', document_uploaded: true });
      setDocumentFile(null);
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao enviar documento');
    } finally {
      setSubmittingClaim(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    try {
      setSubmittingReview(true);
      // IMPORTANTE: usa `business.id` (real do banco) e não `id` (que pode ser
      // um slug SEO como "pizzaria-bella-napoli"). O backend só consulta por
      // `db.businesses.find_one({"id": business_id})`, então slug daria 404.
      const targetId = business?.id || id;
      await reviewsApi.create({
        business_id: targetId,
        rating: reviewForm.rating,
        comment: reviewForm.comment
      });

      setReviewForm({ rating: 5, comment: '' });
      setShowReviewForm(false);
      loadReviews();
      loadRatingStats();
      alert('Avaliação enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao enviar avaliação:', error);
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message || 'Erro ao enviar avaliação';
      alert(msg);
    } finally {
      setSubmittingReview(false);
    }
  };

  // Submeter denúncia
  const handleSubmitReport = async (e) => {
    e.preventDefault();
    
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    if (!reportForm.reason) {
      alert('Selecione um motivo para a denúncia');
      return;
    }

    try {
      setSubmittingReport(true);
      const targetId = business?.id || id;
      await reportsApi.create({
        business_id: targetId,
        reason: reportForm.reason,
        description: reportForm.description
      });

      setReportSuccess(true);
      setReportForm({ reason: '', description: '' });
      
      // Fechar modal após 2 segundos
      setTimeout(() => {
        setShowReportModal(false);
        setReportSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Erro ao enviar denúncia:', error);
      alert(error.response?.data?.detail || 'Erro ao enviar denúncia');
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleWhatsApp = () => {
    if (!business?.whatsapp) return;
    const promocaoAtiva = promotions?.find?.((p) => p && (p.ativo !== false))
      || promotions?.[0]
      || null;
    openWhatsApp({
      business,
      municipio,
      promocao: promocaoAtiva,
    });
  };

  /**
   * Compartilha o negócio via WhatsApp usando texto estruturado da plataforma.
   * URL usa slug SEO (`/{municipio}/{categoria}/{slug}`) quando disponível.
   */
  const buildShareUrlNegocio = () => {
    const catSlug = business.categoria_slug || business.categoria;
    let sharePath;
    if (business.municipio_slug && catSlug && business.slug) {
      sharePath = `/${business.municipio_slug}/${catSlug}/${business.slug}`;
    } else if (business.slug) {
      sharePath = `/negocio/${business.slug}`;
    } else {
      sharePath = `/negocio/${business.id}`;
    }
    return `${window.location.origin}${sharePath}`;
  };

  const buildShareTextNegocio = (url) => [
    `💛 Conheça ${business.nome}`,
    '',
    'Esse negócio faz parte da nossa comunidade e ajuda a movimentar a economia local com produtos, serviços e atendimento que valorizam a cidade.',
    '',
    'Quando você conhece, compartilha e compra de um negócio local, fortalece quem trabalha perto de você e ajuda novas oportunidades a circularem aqui.',
    '',
    `Acesse e conheça: ${url}`,
  ].join('\n');

  const handleShareNegocio = async () => {
    if (!business) return;
    setSharingBiz(true);
    setTimeout(() => setSharingBiz(false), 900);
    const url = buildShareUrlNegocio();
    const text = buildShareTextNegocio(url);
    try {
      if (navigator.share) {
        await navigator.share({ title: business.nome, text });
      } else {
        const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
        window.open(wa, '_blank', 'noopener,noreferrer');
      }
    } catch {
      /* user cancelled */
    }
  };

  const handleCopyLinkNegocio = async () => {
    if (!business) return;
    try {
      await navigator.clipboard.writeText(buildShareUrlNegocio());
      setCopiedBiz(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopiedBiz(false), 2200);
    } catch {
      toast.error('Não foi possível copiar o link.');
    }
  };

  const handleInstagram = () => {
    if (business?.instagram) {
      const username = business.instagram.replace('@', '');
      window.open(`https://instagram.com/${username}`, '_blank');
    }
  };

  const handleCall = () => {
    if (business?.telefone || business?.telefone_fixo) {
      window.location.href = `tel:${business.telefone || business.telefone_fixo}`;
    }
  };

  const handleNavigate = () => {
    if (business?.latitude && business?.longitude) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${business.latitude},${business.longitude}`, '_blank');
    }
  };

  const diasSemana = {
    'segunda': 'Segunda-feira',
    'terca': 'Terça-feira',
    'quarta': 'Quarta-feira',
    'quinta': 'Quinta-feira',
    'sexta': 'Sexta-feira',
    'sabado': 'Sábado',
    'domingo': 'Domingo'
  };

  const estacionamentoLabels = {
    'proprio': 'Estacionamento próprio',
    'conveniado': 'Estacionamento conveniado',
    'rua': 'Estacionamento na rua'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--background)] pb-20 flex items-center justify-center">
        <div className="inline-block w-12 h-12 border-4 border-[var(--primary)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !business) {
    return (
      <div className="min-h-screen bg-[var(--background)] pb-20 pt-8">
        <div className="max-w-2xl mx-auto px-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[var(--primary)] hover:underline mb-4"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
            Voltar
          </button>
          <div className="bg-white rounded-2xl p-12 text-center">
            <p className="text-xl text-[var(--text-secondary)]">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Collect amenities that are true
  const amenities = [];
  if (business.acessibilidade) amenities.push({ icon: Accessibility, label: "Acessibilidade", color: "blue" });
  if (business.wifi_gratis) amenities.push({ icon: Wifi, label: "Wi-Fi grátis", color: "blue" });
  if (business.pet_friendly) amenities.push({ icon: Dog, label: "Pet friendly", color: "green" });
  if (business.espaco_kids) amenities.push({ icon: Baby, label: "Espaço kids", color: "pink" });
  if (business.ar_condicionado) amenities.push({ icon: Snowflake, label: "Ar-condicionado", color: "blue" });
  if (business.area_externa) amenities.push({ icon: TreeDeciduous, label: "Área externa", color: "green" });
  if (business.seguranca) amenities.push({ icon: ShieldCheck, label: "Segurança", color: "gray" });
  if (business.banheiro_publico) amenities.push({ icon: Building, label: "Banheiro público", color: "gray" });
  if (business.estacionamento && business.estacionamento !== 'nenhum') {
    amenities.push({ icon: Car, label: estacionamentoLabels[business.estacionamento] || "Estacionamento", color: "gray" });
  }

  // Collect payment methods
  const paymentMethods = [];
  if (business.aceita_dinheiro) paymentMethods.push("Dinheiro");
  if (business.aceita_cartao_credito) paymentMethods.push("Cartão de crédito");
  if (business.aceita_cartao_debito) paymentMethods.push("Cartão de débito");
  if (business.aceita_pix) paymentMethods.push("PIX");
  if (business.aceita_convenios) paymentMethods.push("Convênios de saúde");

  // Collect differentials
  const differentials = [];
  if (business.producao_artesanal) differentials.push({ icon: "🎨", label: "Produção artesanal" });
  if (business.produtos_sustentaveis) differentials.push({ icon: "🌿", label: "Produtos sustentáveis" });
  if (business.cooperativa) differentials.push({ icon: "🤝", label: "Cooperativa/Associação" });
  if (business.empresa_familiar) differentials.push({ icon: "👨‍👩‍👧‍👦", label: "Empresa familiar" });

  // Social impact
  const socialImpact = [];
  if (business.projetos_sociais) socialImpact.push("Participa de projetos sociais");
  if (business.emprega_comunidade) socialImpact.push("Emprega mão de obra local");
  if (business.trabalha_inclusao) socialImpact.push("Trabalha com inclusão");
  if (business.apoia_cultura) socialImpact.push("Apoia artistas/cultura local");

  // Resolved logo URL: new system imagens.logo > legacy avatar_url
  const logoUrl = business.imagens?.logo?.medium
    || business.imagens?.logo?.thumb
    || business.avatar_url
    || null;

  // Resolved gallery URLs: new system imagens.galeria (objects) + legacy images (strings)
  const galleryUrls = [
    ...((business.imagens?.galeria || [])
      .map((img) => img.medium || img.large || img.thumb)
      .filter(Boolean)),
    ...((business.images || []).filter((u) => typeof u === 'string' && u)),
  ];

  // Privacidade do endereço: quando o dono pediu para não exibir o endereço
  // publicamente, ocultamos o mapa e o botão "Como chegar", mostrando
  // apenas bairro + município no bloco de localização.
  // Obs: `aproximada` mantém o mapa (com coordenadas intencionalmente
  // aproximadas) e o próprio badge de aviso.
  const _priv = business.privacidade_localizacao || 'endereco_completo';
  const _tipo = business.tipo_localizacao || 'area_urbana';
  const enderecoOculto = (
    business.ocultar_endereco === true ||
    ['apenas_comunidade', 'apenas_distrito'].includes(_priv) ||
    ['domicilio', 'online', 'sem_endereco_fixo'].includes(_tipo)
  );

  return (
    <div className="min-h-screen bg-[var(--background)] pb-20">
      {/* SEO Head */}
      {business && (
        <SEOHead
          title={`${business.nome}${municipio ? ` em ${municipio.nome}, ${municipio.uf || 'MG'}` : ''}`}
          description={(() => {
            const base = business.descricao_curta || business.descricao?.slice(0, 160) || `${business.nome} - Encontre no Empreende Comunidade`;
            // Incluir top-3 tags controladas na meta description para SEO
            // ("oficina moto Honda", "comida japonesa Ponte Nova" etc.)
            const tags = business.tags_controladas_ids || [];
            if (tags.length === 0) return base;
            const labels = tags.slice(0, 3).map((s) =>
              s.split('__').pop().replace(/-/g, ' '),
            );
            const suffix = ` · Especialidades: ${labels.join(', ')}`;
            return (base.length + suffix.length <= 200)
              ? `${base}${suffix}`
              : base;
          })()}
          canonical={(() => {
            const cs = business.categoria_slug || business.categoria;
            if (business.municipio_slug && cs && business.slug) {
              return `/${business.municipio_slug}/${cs}/${business.slug}`;
            }
            return `/negocio/${business.slug || business.id}`;
          })()}
          image={logoUrl || (galleryUrls[0])}
          preloadImage={logoUrl || galleryUrls[0]}
          type="place"
          jsonLd={[
            buildLocalBusinessJsonLd(business, municipio),
            buildBreadcrumbJsonLd((() => {
              const norm = (x) => (x || '').toString().normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '').toLowerCase()
                .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-+|-+$/g, '');
              const catSlug = business.categoria_slug || business.categoria;
              return [
                { name: 'Início', url: '/' },
                ...(municipio ? [{ name: municipio.nome, url: `/${municipio.slug}` }] : []),
                ...(business.categoria_nome && business.municipio_slug
                  ? [{ name: business.categoria_nome, url: `/${business.municipio_slug}/${catSlug}` }]
                  : []),
                ...(business.subcategoria && business.categoria_nome && business.municipio_slug
                  ? [{ name: business.subcategoria, url: `/${business.municipio_slug}/${catSlug}/${norm(business.subcategoria)}` }]
                  : []),
                { name: business.nome },
              ];
            })()),
          ]}
        />
      )}
      {/* Header with Breadcrumbs (item #12: Início › Cidade › Categoria › Negócio) */}
      <div className="bg-gradient-to-r from-[var(--primary)] to-purple-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Breadcrumbs
            items={[
              ...(business.categoria_nome
                ? [{
                    label: business.categoria_nome,
                    // Rota SEO-friendly canônica: /:municipio/:categoria-slug
                    // Backend e frontend aceitam tanto categoria id quanto slug,
                    // mas preferimos o slug pretty para URL canonical (SEO).
                    path: business.municipio_slug
                      ? `/${business.municipio_slug}/${business.categoria_slug || business.categoria}`
                      : `/explorar?categoria=${business.categoria || business.categoria_slug}`,
                  }]
                : []),
              ...(business.subcategoria && business.categoria_nome && business.municipio_slug
                ? [(() => {
                    // Slugify the subcategoria name to build the canonical URL
                    // /:municipio/:categoria-slug/:subcategoria-slug
                    const norm = (x) => (x || '').toString().normalize('NFD')
                      .replace(/[\u0300-\u036f]/g, '').toLowerCase()
                      .replace(/[^\w\s-]/g, '').replace(/[-\s]+/g, '-').replace(/^-+|-+$/g, '');
                    const catSlug = business.categoria_slug || business.categoria;
                    return {
                      label: business.subcategoria,
                      path: `/${business.municipio_slug}/${catSlug}/${norm(business.subcategoria)}`,
                    };
                  })()]
                : []),
              { label: business.nome, path: null },
            ]}
            municipio={municipio}
            className="mb-2"
          />
        </div>
      </div>
      
      {/* Sub-header with actions */}
      <div className="bg-white border-b border-[var(--border)] sticky top-14 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[var(--primary)] hover:underline"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              Voltar
            </button>
            <div className="flex items-center gap-2">
              <ShareButton business={business} tipo="negocio" municipio={municipio} size="md" />
              <FavoriteButton business={business} size="md" />
              {/* Botão de Denúncia */}
              <button
                onClick={() => setShowReportModal(true)}
                className="p-2 rounded-full bg-white/90 hover:bg-red-50 text-gray-600 hover:text-red-600 transition-colors"
                title="Denunciar este negócio"
                data-testid="report-button"
              >
                <Flag className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden" data-testid="business-details">
          
          {/* Hero Section */}
          <div className="p-6 border-b border-[var(--border)]">
            <div className="flex items-start gap-4">
              {logoUrl && (
                <img
                  src={resolveBackendImage(logoUrl)}
                  alt={business.nome}
                  className="w-20 h-20 rounded-xl object-contain object-center block border-2 border-[var(--border)] bg-white"
                />
              )}
              <div className="flex-1">
                <h1 className="text-3xl font-bold mb-1" style={{ fontFamily: 'Manrope' }}>
                  {business.nome}
                </h1>
                {business.nome_fantasia && business.nome_fantasia !== business.nome && (
                  <p className="text-lg text-[var(--text-secondary)] mb-1">
                    {business.nome_fantasia}
                  </p>
                )}
                <p className="text-[var(--text-secondary)]">
                  {business.subcategoria || business.categoria} • {business.bairro}
                </p>
                {business.tags_controladas_ids && business.tags_controladas_ids.length > 0 && (
                  <div className="mt-3">
                    <BusinessTagsChips
                      business={business}
                      limit={4}
                      size="md"
                      expandable
                      testIdPrefix="detalhes-tag"
                    />
                  </div>
                )}
                <div className="flex items-center gap-4 mt-2">
                  {business.ano_fundacao && (
                    <p className="text-sm text-[var(--text-muted)]">
                      Desde {business.ano_fundacao}
                    </p>
                  )}
                  <div className="flex items-center gap-1 text-sm text-[var(--text-muted)]">
                    <Eye className="w-4 h-4" />
                    <span>{(business.views_count || 0).toLocaleString()} visualizações</span>
                  </div>
                  {(business.favorites_count > 0) && (
                    <div className="flex items-center gap-1 text-sm text-pink-600">
                      <Heart className="w-4 h-4 fill-current" />
                      <span>{business.favorites_count} {business.favorites_count === 1 ? 'pessoa favoritou' : 'pessoas favoritaram'}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Main Badges */}
            <div className="flex flex-wrap gap-2 mt-4">
              {/* Botão "Editar negócio" — donos abrem o painel Lite in-page.
                  Admins municipais vão para o editor completo em
                  /admin/negocios/:id/editar.
                  NOTE: business.owner_id historicamente armazena o EMAIL do
                  dono (não o user.id), então comparamos ambos. */}
              {user && (
                business.is_owner
                || (business.owner_id && (business.owner_id === user.id || business.owner_id === user.email))
                || ['adm_master', 'gestor', 'tecnico'].includes(user.role)
              ) && (
                <button
                  onClick={() => {
                    const isAdmin = ['adm_master', 'gestor', 'tecnico'].includes(user.role);
                    const isOwner = business.is_owner || (business.owner_id && (business.owner_id === user.id || business.owner_id === user.email));
                    if (isOwner && !isAdmin) {
                      setShowLiteEditor(true);
                    } else {
                      navigate(`/admin/negocios/${business.id}/editar`);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-600 text-white hover:bg-purple-700"
                  data-testid="business-edit-btn"
                >
                  <Edit2 className="w-4 h-4" />
                  Editar negócio
                </button>
              )}
              {/* Badge "Verificado" removido da exibição pública (iter 196).
                  Sub-critério continua no IRN e visível em telas admin. */}
              {business.entrega && (
                <FeatureBadge label="🛵 Entrega" color="purple" />
              )}
              {business.atende_domicilio && (
                <FeatureBadge label="🏠 Atende em domicílio" color="indigo" />
              )}
              {business.promocao && (
                <FeatureBadge label="🏷️ Promoção" color="orange" />
              )}
              {business.esta_contratando && (
                <FeatureBadge icon={Briefcase} label="Contratando" color="teal" />
              )}
              {business.apenas_agendamento && (
                <FeatureBadge icon={Calendar} label="Só com agendamento" color="gray" />
              )}
              {business.emergencial_24h && (
                <FeatureBadge icon={AlertCircle} label="24h / Emergencial" color="orange" />
              )}
            </div>

            {/* Presence Type */}
            <div className="flex flex-wrap gap-2 mt-3">
              {business.loja_fisica && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">🏪 Loja física</span>
              )}
              {business.atendo_em_casa && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">🏠 Atende em casa</span>
              )}
              {business.itinerante && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">🚚 Itinerante</span>
              )}
              {business.apenas_online && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">💻 Apenas online</span>
              )}
            </div>

            {/* Achievement Badges - Gamification */}
            {business.id && (
              <div className="mt-4 space-y-2">
                <BusinessLevelBadge businessId={business.id} />
                <BusinessBadgesInline businessId={business.id} max={5} />
              </div>
            )}
            {business.badges && business.badges.length > 0 && (
              <div className="mt-2">
                <div className="flex flex-wrap gap-2">
                  {business.badges.map((badge) => (
                    <span
                      key={badge}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-purple-100 to-pink-100 text-purple-900 border border-purple-300"
                    >
                      {badge}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Management Info & Claim Button */}
            {managementInfo && (
              <div className="mt-4 flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <Users className="w-4 h-4" />
                  <span>{managementInfo.management_label}</span>
                </div>
                {managementInfo.can_claim && (
                  <button
                    onClick={handleStartClaim}
                    className="text-sm text-[var(--primary)] hover:underline font-medium"
                    data-testid="claim-business-btn"
                  >
                    Sou o proprietário
                  </button>
                )}
              </div>
            )}

            {/* Distance & Rating */}
            <div className="flex items-center gap-4 mt-4">
              {business.distancia_km !== null && business.distancia_km !== undefined && (
                <p className="text-sm text-[var(--text-muted)] flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {business.distancia_km.toFixed(1)} km de você
                </p>
              )}
              {ratingStats && ratingStats.count > 0 && (
                <div className="flex items-center gap-2">
                  <StarRating rating={ratingStats.average} size="md" showNumber={true} />
                  <span className="text-sm text-[var(--text-secondary)]">
                    ({ratingStats.count})
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Feed do empreendedor — posts recentes antes do Sobre.
              Owners também veem um CTA "Publicar novidade" mesmo sem posts. */}
          {(() => {
            const isOwner = !!business.is_owner;
            const isAdmin = user && ['adm_master', 'gestor', 'tecnico'].includes(user.role);
            const canPost = isOwner || isAdmin;
            if (feedPosts.length === 0 && !canPost) return null;
            return (
              <section className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="flex items-center justify-between gap-3 mb-3 pb-2 border-b border-slate-100 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Megaphone className="w-5 h-5 text-purple-600" />
                    <h2 className="text-lg font-bold text-gray-900">Novidades do negócio</h2>
                    {feedPosts.length > 0 && (
                      <span className="text-xs text-gray-500 font-medium">({feedPosts.length})</span>
                    )}
                  </div>
                  {canPost && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => navigate(`/empreendedor/feed/novo?negocio_id=${encodeURIComponent(business.id)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 shadow-sm"
                        data-testid="business-feed-publish-btn"
                      >
                        <Plus className="w-4 h-4" />
                        Publicar novidade
                      </button>
                      <button
                        onClick={() => navigate(`/perfil?tab=negocios&new=promo&negocio_id=${encodeURIComponent(business.id)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
                        data-testid="business-publish-promo-btn"
                      >
                        <Tag className="w-4 h-4" />
                        Publicar promoção
                      </button>
                      <button
                        onClick={() => navigate(`/perfil?tab=vagas&new=vaga&negocio_id=${encodeURIComponent(business.id)}`)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm"
                        data-testid="business-publish-job-btn"
                      >
                        <Briefcase className="w-4 h-4" />
                        Publicar vaga
                      </button>
                    </div>
                  )}
                </div>
                {feedPosts.length > 0 ? (
                  <div className="space-y-3 max-w-2xl mx-auto" data-testid="business-feed-section">
                    {feedPosts.map(post => (
                      <FeedCard
                        key={post.id}
                        post={post}
                        variant="full"
                        business={business}
                        categoriasMap={feedCategoriasMap}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6" data-testid="business-feed-empty">
                    <p className="text-sm text-gray-600">Ainda não há novidades publicadas.</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Publique novidades, promoções ou vagas usando os botões acima — elas aparecem aqui e no feed da cidade.
                    </p>
                  </div>
                )}
              </section>
            );
          })()}

          {/* Description — rich-text HTML sanitised before render */}
          <Section title="Sobre" icon={Building}>
            {business.descricao_curta && (
              <p className="text-lg font-medium text-[var(--text-primary)] mb-3">
                {business.descricao_curta}
              </p>
            )}
            {(() => {
              const raw = business.descricao || '';
              const looksLikeHtml = /<\/?[a-z][^>]*>/i.test(raw);
              if (looksLikeHtml) {
                const clean = DOMPurify.sanitize(raw, {
                  ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'h1', 'h2', 'ul', 'ol', 'li', 'span'],
                  ALLOWED_ATTR: ['data-list'],
                });
                // Sanitiza `&nbsp;` (pasted from Word/Google Docs) que impede
                // quebra de linha e faz o texto vazar do container clipado
                // por `overflow-hidden` do card pai (bug reportado iter 216).
                const cleanNoNbsp = clean
                  .replace(/&nbsp;/gi, ' ')
                  .replace(/&#160;/gi, ' ')
                  .replace(/\u00A0/g, ' ')
                  .replace(/ {2,}/g, ' ');
                return (
                  <>
                    <div
                      className="rich-html prose prose-sm max-w-none text-[var(--text-secondary)] leading-relaxed break-words [text-wrap:pretty] min-w-0"
                      data-testid="business-descricao-rendered"
                      dangerouslySetInnerHTML={{ __html: cleanNoNbsp }}
                    />
                    <style>{`
                      .rich-html h1 { font-size: 1.5rem; font-weight: 700; margin: 0.75rem 0 0.5rem; }
                      .rich-html ul { list-style: disc; margin-left: 1.25rem; }
                      .rich-html ol { list-style: decimal; margin-left: 1.25rem; }
                      /* Quill 2.x serializes bullet lists as <ol><li data-list="bullet"> — restore disc bullets */
                      .rich-html ol:has(li[data-list="bullet"]) { list-style: none; padding-left: 0; }
                      .rich-html li[data-list="bullet"] { list-style-position: outside; margin-left: 1.25rem; }
                      .rich-html li[data-list="bullet"]::before { content: "•"; margin-right: 0.5rem; color: var(--primary, #5b21b6); }
                      .rich-html p { margin: 0.25rem 0; }
                      .rich-html strong { font-weight: 700; }
                      .rich-html em { font-style: italic; }
                    `}</style>
                  </>
                );
              }
              return (
                <p className="text-[var(--text-secondary)] leading-relaxed whitespace-pre-line break-words [text-wrap:pretty] min-w-0">
                  {raw
                    .replace(/\u00A0/g, ' ')
                    .replace(/ {2,}/g, ' ')}
                </p>
              );
            })()}

            {/* Tags — clickable chips routing to /tags/:slug (item #7) */}
            {business.tags && business.tags.length > 0 && (
              <div className="mt-4" data-testid="business-tags-chips">
                <div className="flex flex-wrap gap-2">
                  {business.tags.map((tag) => {
                    const tagSlug = (tag || '')
                      .normalize('NFKD')
                      .replace(/[\u0300-\u036f]/g, '')
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, '')
                      .replace(/[-\s]+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    if (!tagSlug) return null;
                    const base = business.municipio_slug ? `/${business.municipio_slug}/tags` : '/tags';
                    return (
                      <Link
                        key={tag}
                        to={`${base}/${tagSlug}`}
                        className="px-3 py-1 bg-[var(--primary)]/10 text-[var(--primary)] hover:bg-[var(--primary)]/20 rounded-full text-sm transition-colors active:scale-95"
                        data-testid={`business-tag-${tagSlug}`}
                      >
                        {tag}
                      </Link>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Secondary Categories */}
            {business.categorias_secundarias && business.categorias_secundarias.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-[var(--text-muted)] mb-2">Também atua em:</p>
                <div className="flex flex-wrap gap-2">
                  {business.categorias_secundarias.map((cat) => (
                    <span
                      key={cat}
                      className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                    >
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Section>

          {/* Image Gallery */}
          {galleryUrls.length > 0 && (
            <Section title="Fotos" icon={null}>
              <ImageGallery images={galleryUrls} businessName={business.nome} />
            </Section>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <Section title="Comodidades" icon={CheckCircle}>
              <div className="flex flex-wrap gap-2">
                {amenities.map((amenity) => (
                  <FeatureBadge key={amenity.label} icon={amenity.icon} label={amenity.label} color={amenity.color} />
                ))}
              </div>
            </Section>
          )}

          {/* Payment Methods */}
          {paymentMethods.length > 0 && (
            <Section title="Formas de Pagamento" icon={CreditCard}>
              <div className="flex flex-wrap gap-2">
                {paymentMethods.map((method) => (
                  <span key={method} className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                    {method}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Differentials */}
          {(differentials.length > 0 || (business.certificacoes && business.certificacoes.length > 0)) && (
            <Section title="Diferenciais" icon={Award}>
              {differentials.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {differentials.map((diff) => (
                    <span key={diff.label} className="px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      {diff.icon} {diff.label}
                    </span>
                  ))}
                </div>
              )}
              {business.certificacoes && business.certificacoes.length > 0 && (
                <div>
                  <p className="text-sm text-[var(--text-muted)] mb-2">Certificações:</p>
                  <div className="flex flex-wrap gap-2">
                    {business.certificacoes.map((cert) => (
                      <span key={cert} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        ✓ {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Social Impact */}
          {(socialImpact.length > 0 || (business.iniciativas_sustentaveis && business.iniciativas_sustentaveis.length > 0)) && (
            <Section title="Impacto Social" icon={Heart}>
              {socialImpact.length > 0 && (
                <div className="space-y-2 mb-3">
                  {socialImpact.map((impact) => (
                    <div key={impact} className="flex items-center gap-2 text-[var(--text-secondary)]">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      {impact}
                    </div>
                  ))}
                </div>
              )}
              {business.iniciativas_sustentaveis && business.iniciativas_sustentaveis.length > 0 && (
                <div>
                  <p className="text-sm text-[var(--text-muted)] mb-2">Iniciativas sustentáveis:</p>
                  <div className="flex flex-wrap gap-2">
                    {business.iniciativas_sustentaveis.map((init) => (
                      <span key={init} className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                        🌱 {init}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Hiring Section - New Jobs Module */}
          {jobs.length > 0 && (
            <Section title="Vagas Abertas" icon={Briefcase} className="bg-teal-50">
              <div className="space-y-3">
                {jobs.map((job) => {
                  const links = job.application_links || {};
                  return (
                    <div key={job.id} className="p-4 bg-white rounded-xl border border-teal-200">
                      <h4 className="font-bold text-lg text-teal-800">{job.titulo}</h4>
                      <p className="text-[var(--text-secondary)] mt-1">{job.descricao}</p>
                      
                      <div className="flex flex-wrap items-center gap-3 mt-3">
                        {links.email && (
                          <a 
                            href={`mailto:${links.email}`}
                            className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline"
                          >
                            <Mail className="w-4 h-4" />
                            Email
                          </a>
                        )}
                        {links.whatsapp && (
                          <a 
                            href={`https://wa.me/${links.whatsapp.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline"
                          >
                            <Phone className="w-4 h-4" />
                            WhatsApp
                          </a>
                        )}
                        {links.website && (
                          <a 
                            href={links.website.startsWith('http') ? links.website : `https://${links.website}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline"
                          >
                            <Globe className="w-4 h-4" />
                            Site
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-500 mt-3">
                        Válida até {new Date(job.data_fim + 'T00:00:00').toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  );
                })}
              </div>
            </Section>
          )}

          {/* Location */}
          <Section title="Localização" icon={MapPin}>
            <div className="flex items-start gap-3 mb-4">
              <MapPin className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-1" />
              <div>
                {(() => {
                  // Monta o endereço público conforme tipo_localizacao + privacidade_localizacao
                  const mun = business.municipio_nome || municipio?.nome || '';
                  const tipo = business.tipo_localizacao || 'area_urbana';
                  const priv = business.privacidade_localizacao || 'endereco_completo';
                  const junta = (...parts) => parts.filter(Boolean).join(' – ');
                  let principal = '';
                  let secundario = '';
                  if (priv === 'apenas_comunidade' && business.comunidade) {
                    principal = junta(business.comunidade, mun);
                  } else if (priv === 'apenas_distrito' && (business.distrito || business.comunidade)) {
                    principal = junta(business.distrito || business.comunidade, mun);
                  } else if (business.ocultar_endereco) {
                    principal = junta(business.bairro, mun);
                  } else if (tipo === 'comunidade_rural') {
                    const ref = business.ponto_referencia
                      ? (/^(próximo|proximo|perto|ao lado|em frente|atrás|atras|vizinho)/i.test(business.ponto_referencia.trim())
                        ? business.ponto_referencia
                        : `Próximo a ${business.ponto_referencia}`)
                      : 'Zona Rural';
                    principal = junta(business.comunidade, ref, mun);
                    secundario = business.distrito ? `Distrito de ${business.distrito}` : '';
                  } else if (tipo === 'distrito') {
                    principal = junta(business.endereco, business.distrito, mun);
                  } else if (tipo === 'domicilio') {
                    principal = junta('Atendimento em domicílio', mun);
                  } else if (tipo === 'online') {
                    principal = junta('Atendimento online', mun);
                  } else if (tipo === 'sem_endereco_fixo') {
                    principal = junta('Sem endereço fixo', mun);
                  } else {
                    principal = business.endereco;
                    secundario = junta(business.bairro, mun);
                  }
                  return (
                    <>
                      <p className="text-[var(--text-primary)] font-medium" data-testid="endereco-publico">{principal}</p>
                      {secundario && <p className="text-sm text-[var(--text-secondary)]">{secundario}</p>}
                      {business.localizacao_aproximada && (
                        <p className="text-xs text-[var(--text-muted)] mt-1" data-testid="localizacao-aproximada-badge">
                          📍 Localização aproximada (a pedido do responsável)
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
            {business.area_cobertura && (
              <p className="text-sm text-[var(--text-muted)] mb-4">
                <strong>Área de cobertura:</strong> {business.area_cobertura}
              </p>
            )}
            <button
              onClick={handleNavigate}
              className="flex items-center gap-2 text-[var(--primary)] hover:underline font-medium"
              data-testid="navigate-button"
              hidden={enderecoOculto}
              style={enderecoOculto ? { display: 'none' } : undefined}
            >
              <Navigation className="w-4 h-4" />
              Como chegar
            </button>
          </Section>

          {/* Hours - Compact version */}
          {business.horarios && business.horarios.length > 0 && (
            <Section title="Horário de Funcionamento" icon={Clock}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                {business.horarios.map((horario) => (
                  <div key={horario.dia} className="flex items-center justify-between py-1">
                    <span className="text-[var(--text-primary)] font-medium text-sm">
                      {diasSemana[horario.dia] || horario.dia}
                    </span>
                    <span className={`text-sm ${horario.fechado ? 'text-red-500' : 'text-[var(--text-secondary)]'}`}>
                      {horario.fechado ? 'Fechado' : `${horario.abre} - ${horario.fecha}`}
                    </span>
                  </div>
                ))}
              </div>
              {business.horarios_especiais && (
                <p className="text-xs text-[var(--text-muted)] mt-3 p-2 bg-yellow-50 rounded-lg">
                  <strong>Horários especiais:</strong> {business.horarios_especiais}
                </p>
              )}
            </Section>
          )}

          {/* Contact */}
          <Section title="Contato" icon={Phone}>
            <div className="space-y-3">
              {business.whatsapp && (
                <button
                  onClick={handleWhatsApp}
                  className="w-full bg-[var(--secondary)] text-[var(--secondary-foreground)] hover:bg-[#FF9500] rounded-xl px-6 py-4 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-3"
                  data-testid="whatsapp-contact-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  Enviar mensagem no WhatsApp
                </button>
              )}

              {/* Compartilhar negócio no WhatsApp / redes — texto estruturado
                  da plataforma, com URL semântica quando disponível. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={handleShareNegocio}
                  disabled={sharingBiz}
                  className={`relative overflow-hidden bg-white hover:bg-purple-50 text-[var(--primary)] border-2 border-[var(--primary)]/20 hover:border-[var(--primary)]/40 rounded-xl px-4 py-3 font-semibold transition-all flex items-center justify-center gap-2 active:scale-95 ${
                    sharingBiz ? 'share-btn-pulse' : ''
                  }`}
                  data-testid="btn-share-negocio"
                >
                  <Share2 className={`w-4 h-4 transition-transform ${sharingBiz ? 'share-icon-fly' : ''}`} />
                  <span>Compartilhar</span>
                  {sharingBiz && (
                    <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <span className="share-ripple" />
                    </span>
                  )}
                </button>
                <button
                  onClick={handleCopyLinkNegocio}
                  className="bg-white hover:bg-purple-50 text-[var(--primary)] border-2 border-[var(--primary)]/20 hover:border-[var(--primary)]/40 rounded-xl px-4 py-3 font-semibold transition-all flex items-center justify-center gap-2 active:scale-95"
                  data-testid="btn-copy-link-negocio"
                >
                  {copiedBiz ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                  <span>{copiedBiz ? 'Copiado!' : 'Copiar link'}</span>
                </button>
              </div>

              {/* Cardápio Delivery interno (Empreende Delivery) — CTA primário quando
                  o negócio tem delivery ativado na plataforma. Tem precedência sobre
                  link_delivery externo (que é mantido como secundário). */}
              {business.delivery_ativo && business.municipio_slug && business.slug && municipioDeliveryEnabled !== false && (
                <Link
                  to={`/delivery/${business.municipio_slug}/${business.slug}`}
                  className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 rounded-xl px-6 py-4 font-bold transition-all shadow-md hover:shadow-lg active:scale-95 flex items-center justify-center gap-3"
                  data-testid="delivery-cardapio-button"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Peça pelo cardápio digital
                  <span className="text-[10px] uppercase font-semibold bg-white/25 px-2 py-0.5 rounded-full">
                    Novo
                  </span>
                </Link>
              )}

              {/* Item #1: Link de delivery (iFood, Rappi, marketplace, App Store, Play, etc.) */}
              {business.link_delivery && (
                <a
                  href={business.link_delivery.startsWith('http') ? business.link_delivery : `https://${business.link_delivery}`}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="w-full bg-purple-600 text-white hover:bg-purple-700 rounded-xl px-6 py-4 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 flex items-center justify-center gap-3"
                  data-testid="delivery-link-button"
                >
                  <ShoppingBag className="w-5 h-5" />
                  Pedir delivery (link externo)
                </a>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                {business.instagram && (
                  <button
                    onClick={handleInstagram}
                    className="border-2 border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 rounded-xl px-4 py-3 font-medium transition-all flex items-center justify-center gap-2"
                    data-testid="instagram-contact-button"
                  >
                    <Instagram className="w-5 h-5" />
                    Instagram
                  </button>
                )}
                
                {(business.telefone || business.telefone_fixo) && (
                  <button
                    onClick={handleCall}
                    className="border-2 border-[var(--border)] hover:border-[var(--primary)] hover:bg-[var(--primary)]/5 rounded-xl px-4 py-3 font-medium transition-all flex items-center justify-center gap-2"
                    data-testid="phone-contact-button"
                  >
                    <Phone className="w-5 h-5" />
                    Ligar
                  </button>
                )}
              </div>

              {/* Additional contact info */}
              <div className="mt-4 space-y-2 text-sm">
                {business.email && (
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${business.email}`} className="hover:text-[var(--primary)]">
                      {business.email}
                    </a>
                  </div>
                )}
                {business.site && (
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Globe className="w-4 h-4" />
                    <a href={business.site} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)] flex items-center gap-1">
                      Site oficial <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {business.loja_online && (
                  <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                    <Globe className="w-4 h-4" />
                    <a href={business.loja_online} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--primary)] flex items-center gap-1">
                      Loja online <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
                {business.telefone_fixo && (
                  <div className="flex items-center gap-2 text-[var(--text-muted)]">
                    <Phone className="w-4 h-4" />
                    Telefone: {business.telefone_fixo}
                  </div>
                )}
              </div>
            </div>
          </Section>

          {/* Google Maps */}
          {!enderecoOculto && business.latitude && business.longitude && (
            <div className="p-6 border-b border-[var(--border)]" data-testid="business-map-section">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
                Mapa
              </h2>
              <div className="rounded-xl overflow-hidden shadow-md">
                <iframe
                  src={`https://www.google.com/maps?q=${business.latitude},${business.longitude}&z=15&output=embed`}
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização do negócio"
                  data-testid="google-maps-iframe"
                />
              </div>
            </div>
          )}

          {/* Comment Section */}
          <CommentSection 
            businessId={id} 
            businessOwnerId={business?.owner_id} 
          />

          {/* Coletivos que o negócio faz parte */}
          {coletivosParticipando.length > 0 && (
            <div className="p-6 border-b" data-testid="business-coletivos-section">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4" style={{ fontFamily: 'Manrope' }}>
                <Handshake className="w-5 h-5 text-purple-600" />
                Faz parte de
                <span className="text-sm font-normal text-muted-foreground">({coletivosParticipando.length})</span>
              </h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {coletivosParticipando.map((c) => (
                  <Link
                    key={c.id}
                    to={`/coletivos/${c.slug}`}
                    data-testid={`business-coletivo-${c.id}`}
                    className="group flex items-center gap-3 p-3 rounded-xl border border-gray-100 hover:border-purple-300 hover:shadow-sm transition bg-white"
                  >
                    {c.foto_perfil ? (
                      <img src={resolveBackendImage(c.foto_perfil)} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 shrink-0">
                        <Handshake className="w-6 h-6" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-purple-700">{c.nome}</h3>
                      <div className="text-xs text-gray-500 capitalize">{c.tipo}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Campanhas que o negócio está participando */}
          {campanhasParticipando.length > 0 && (
            <div className="p-6 border-b" data-testid="business-campanhas-section">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4" style={{ fontFamily: 'Manrope' }}>
                <Megaphone className="w-5 h-5 text-purple-600" />
                Participando das promoções
                <span className="text-sm font-normal text-muted-foreground">({campanhasParticipando.length})</span>
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {campanhasParticipando.map((c) => {
                  const cor = c.cor_destaque || '#7C3AED';
                  return (
                    <Link
                      key={c.id}
                      to={`/campanhas/${c.id}`}
                      data-testid={`business-campanha-${c.id}`}
                      className="group block rounded-xl overflow-hidden border border-gray-100 hover:shadow-md hover:border-purple-200 transition bg-white"
                    >
                      <div className="h-1.5" style={{ background: cor }} />
                      <div className="flex gap-3 p-3">
                        {c.arte_visual_url ? (
                          <img
                            src={c.arte_visual_url}
                            alt={c.nome}
                            className="w-20 h-20 rounded-lg object-cover shrink-0"
                          />
                        ) : (
                          <div
                            className="w-20 h-20 rounded-lg flex items-center justify-center shrink-0"
                            style={{ background: `linear-gradient(135deg, ${cor}, ${cor}99)` }}
                          >
                            <Megaphone className="w-7 h-7 text-white/80" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 mb-0.5">
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white"
                              style={{ background: cor }}
                            >
                              {c.status_efetivo === 'ativa' ? 'Ativa' : c.status_efetivo}
                            </span>
                          </div>
                          <h3 className="font-semibold text-sm leading-tight line-clamp-2 group-hover:text-purple-700 transition">
                            {c.nome}
                          </h3>
                          {c.beneficio_descricao && (
                            <div className="mt-1.5 flex items-start gap-1 text-xs text-purple-700">
                              <Sparkles className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="line-clamp-2">{c.beneficio_descricao}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Promotions Section */}
          {promotions.length > 0 && (
            <div ref={promosRef} className="p-6 border-b" data-testid="business-promotions-section">
              <h2 className="text-xl font-bold flex items-center gap-2 mb-4" style={{ fontFamily: 'Manrope' }}>
                <Tag className="w-5 h-5 text-amber-500" />
                Ofertas e Promoções
                <span className="text-sm font-normal text-muted-foreground">({promotions.length})</span>
              </h2>
              <div className="grid gap-3">
                {promotions.map(promo => {
                  const isHighlighted = cupomParam && promo.codigo === cupomParam;
                  const discount = promo.valor_desconto || promo.desconto || 0;
                  const tipo = promo.tipo_desconto || promo.tipo || 'percentual';
                  return (
                    <div
                      key={promo.id || promo.codigo}
                      className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${
                        isHighlighted ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200' : 'border-gray-100 hover:border-gray-200'
                      }`}
                      data-testid={`promo-card-${promo.codigo}`}
                    >
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-black text-sm flex-shrink-0">
                        {tipo === 'percentual' ? `${discount}%` : tipo === 'fixo' ? `R$${discount}` : 'GIFT'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{promo.titulo}</p>
                        {promo.descricao && <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{promo.descricao}</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] font-mono bg-gray-100 px-1.5 py-0.5 rounded">{promo.codigo}</span>
                          {(promo.data_fim || promo.validade) && (
                            <span className="text-[10px] text-gray-400">
                              Até {new Date(promo.data_fim || promo.validade).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/cupom/${promo.codigo}`)}
                        className="bg-[#56007F] hover:bg-[#420060] text-white px-4 py-2 rounded-lg text-xs font-semibold transition-colors flex-shrink-0"
                        data-testid={`redeem-btn-${promo.codigo}`}
                      >
                        Resgatar
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reviews Section */}
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <Star className="w-5 h-5 text-yellow-500" />
                Avaliações
              </h2>
              {user ? (
                <button
                  onClick={() => setShowReviewForm(!showReviewForm)}
                  className="bg-[#FFA52A] hover:bg-[#e89420] text-white font-semibold rounded-full px-5 py-2 text-sm transition-all flex items-center gap-2"
                  data-testid="toggle-review-form"
                >
                  <Star className="w-4 h-4" />
                  {showReviewForm ? 'Cancelar' : 'Avaliar este negócio'}
                </button>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="bg-[#FFA52A] hover:bg-[#e89420] text-white font-semibold rounded-full px-5 py-2 text-sm transition-all flex items-center gap-2"
                  data-testid="login-to-review"
                >
                  <Star className="w-4 h-4" />
                  Avaliar este negócio
                </button>
              )}
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <form onSubmit={handleSubmitReview} className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Sua avaliação</label>
                  <StarRating
                    rating={reviewForm.rating}
                    size="xl"
                    interactive={true}
                    onChange={(rating) => setReviewForm({ ...reviewForm, rating })}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Comentário</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]/20 focus:border-[var(--primary)]"
                    rows={4}
                    placeholder="Compartilhe sua experiência..."
                    required
                    data-testid="review-comment-input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submittingReview}
                  className="bg-[var(--primary)] text-white hover:bg-[#450066] rounded-xl px-6 py-3 font-semibold transition-all shadow-sm hover:shadow-md active:scale-95 disabled:opacity-50 flex items-center gap-2"
                  data-testid="submit-review-button"
                >
                  <Send className="w-4 h-4" />
                  {submittingReview ? 'Enviando...' : 'Enviar avaliação'}
                </button>
              </form>
            )}

            {/* Rating Stats */}
            {ratingStats && ratingStats.count > 0 && (
              <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-4 mb-4">
                  <div className="text-center">
                    <div className="text-4xl font-bold text-[var(--primary)]">
                      {ratingStats.average}
                    </div>
                    <StarRating rating={ratingStats.average} size="md" />
                    <div className="text-sm text-[var(--text-secondary)] mt-1">
                      {ratingStats.count} {ratingStats.count === 1 ? 'avaliação' : 'avaliações'}
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    {[5, 4, 3, 2, 1].map(star => {
                      const count = ratingStats.distribution[star] || 0;
                      const percentage = ratingStats.count > 0 ? (count / ratingStats.count) * 100 : 0;
                      return (
                        <div key={star} className="flex items-center gap-2">
                          <span className="text-sm w-3">{star}</span>
                          <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-yellow-400"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-sm text-[var(--text-secondary)] w-8 text-right">
                            {count}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Reviews List */}
            <div className="space-y-4">
              {reviews.length === 0 ? (
                <p className="text-center text-[var(--text-secondary)] py-8">
                  Ainda não há avaliações. Seja o primeiro a avaliar!
                </p>
              ) : (
                reviews.map((review) => (
                  <div
                    key={review.id}
                    className="p-4 border border-[var(--border)] rounded-xl"
                    data-testid={`review-${review.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-[var(--text-primary)]">
                          {review.user_name}
                        </div>
                        <StarRating rating={review.rating} size="sm" />
                      </div>
                      <span className="text-xs text-[var(--text-muted)]">
                        {new Date(review.created_at).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                    <p className="text-[var(--text-secondary)] leading-relaxed">
                      {review.comment}
                    </p>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Sprint 1: 3 carrosséis de descoberta (similares, recomendados, em alta) */}
        <DiscoveryCarousel
          id="similares"
          title="Negócios parecidos com este"
          subtitle={business ? `${business.categoria_nome || business.categoria || ''}${business.subcategoria ? ` · ${business.subcategoria}` : ''}` : ''}
          negocios={descoberta?.similares?.negocios || []}
          origemId={business?.id}
          tipo="similar"
          municipioId={business?.municipio_id}
          loading={descobertaLoading && !descoberta}
          onAuthRequired={() => toast.info('Faça login para salvar seus negócios favoritos.')}
        />
        <DiscoveryCarousel
          id="recomendados"
          title={descoberta?.recomendados?.label || 'Recomendados para você'}
          negocios={descoberta?.recomendados?.negocios || []}
          origemId={business?.id}
          tipo="recomendado"
          modo={descoberta?.recomendados?.modo}
          municipioId={business?.municipio_id}
          loading={descobertaLoading && !descoberta}
          showRecommendedBadge={descoberta?.recomendados?.modo === 'grafo'}
          onAuthRequired={() => toast.info('Faça login para salvar seus negócios favoritos.')}
        />
        <DiscoveryCarousel
          id="em-alta"
          title={<span className="inline-flex items-center gap-2"><Flame className="w-5 h-5 text-orange-500" />Em alta em {municipio?.nome || 'sua cidade'} esta semana</span>}
          subtitle="Negócios com mais visualizações nos últimos 7 dias"
          negocios={descoberta?.em_alta?.negocios || []}
          origemId={business?.id}
          tipo="em_alta"
          municipioId={business?.municipio_id}
          loading={descobertaLoading && !descoberta}
          showEmAltaBadge={true}
          onAuthRequired={() => toast.info('Faça login para salvar seus negócios favoritos.')}
        />

        {/* Legacy similar businesses (fallback se endpoint novo retornar vazio) */}
        {!recommendations && similarBusinesses.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope' }}>
              Negócios similares
            </h2>
            <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {similarBusinesses.map((similar) => (
                <div 
                  key={similar.id}
                  onClick={() => navigate(buildBusinessUrl(similar))}
                  className="flex-shrink-0 w-64 bg-white rounded-xl border border-[var(--border)] overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                >
                  <div className="h-32 bg-gray-100 relative">
                    {similar.foto || similar.avatar_url ? (
                      <img 
                        src={resolveBackendImage(similar.foto || similar.avatar_url)} 
                        alt={similar.nome}
                        className="w-full h-full object-cover object-center block"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Building className="w-12 h-12" />
                      </div>
                    )}
                    {/* Badge Verificado removido da exibição pública (iter 196). */}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm truncate">{similar.nome}</h3>
                    <p className="text-xs text-[var(--text-secondary)] truncate">{similar.bairro}</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                      <span className="text-xs font-medium">{similar.media_avaliacoes?.toFixed(1) || '—'}</span>
                      <span className="text-xs text-[var(--text-muted)]">
                        • {(similar.views_count || 0).toLocaleString()} views
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal de Denúncia */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {reportSuccess ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Denúncia Enviada</h3>
                <p className="text-gray-600">
                  Obrigado por nos ajudar a manter a qualidade da plataforma. Nossa equipe analisará sua denúncia.
                </p>
              </div>
            ) : (
              <>
                <div className="p-6 border-b flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                      <Flag className="w-5 h-5 text-red-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold">Denunciar Negócio</h3>
                      <p className="text-sm text-gray-500">{business?.nome}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowReportModal(false);
                      setReportForm({ reason: '', description: '' });
                    }}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <form onSubmit={handleSubmitReport} className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Motivo da denúncia *
                    </label>
                    <div className="space-y-2">
                      {REPORT_REASONS.map(reason => (
                        <label
                          key={reason.value}
                          className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                            reportForm.reason === reason.value
                              ? 'border-red-500 bg-red-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="reason"
                            value={reason.value}
                            checked={reportForm.reason === reason.value}
                            onChange={(e) => setReportForm({ ...reportForm, reason: e.target.value })}
                            className="sr-only"
                          />
                          <span className={`w-4 h-4 rounded-full border-2 mr-3 flex items-center justify-center ${
                            reportForm.reason === reason.value
                              ? 'border-red-500 bg-red-500'
                              : 'border-gray-300'
                          }`}>
                            {reportForm.reason === reason.value && (
                              <span className="w-2 h-2 bg-white rounded-full" />
                            )}
                          </span>
                          <span className="text-sm">{reason.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Detalhes adicionais (opcional)
                    </label>
                    <textarea
                      value={reportForm.description}
                      onChange={(e) => setReportForm({ ...reportForm, description: e.target.value })}
                      placeholder="Descreva mais detalhes sobre o problema..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    />
                  </div>

                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>Nota:</strong> Denúncias falsas podem resultar em suspensão da sua conta. 
                      Após 3 denúncias de usuários diferentes, o negócio será enviado para revisão.
                    </p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowReportModal(false);
                        setReportForm({ reason: '', description: '' });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={!reportForm.reason || submittingReport}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {submittingReport ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Flag className="w-4 h-4" />
                          Enviar Denúncia
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Internal Linking Section */}
      {relatedContent && (
        <div className="max-w-4xl mx-auto px-4 mt-8">
          {relatedContent.related_categories?.length > 0 && (
            <div className="mb-6" data-testid="biz-related-categories">
              <h2 className="text-base font-semibold mb-3 text-[var(--text-primary)]">
                Explore mais em {municipio?.nome || 'sua cidade'}
              </h2>
              <div className="flex flex-wrap gap-2">
                {relatedContent.related_categories.map(cat => {
                  const Icon = getCategoryIcon(cat.id);
                  return (
                    <Link
                      key={cat.id}
                      to={cat.url}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--background-elevated)] border border-[var(--border)] rounded-full text-sm text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                    >
                      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                      {cat.nome} ({cat.count})
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
          {relatedContent.related_tags?.length > 0 && (
            <div className="mb-6" data-testid="biz-related-tags">
              <h3 className="text-sm font-semibold mb-2 text-[var(--text-secondary)]">
                Tags relacionadas
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {relatedContent.related_tags.map(t => (
                  <Link
                    key={t.slug}
                    to={`/tags/${t.slug}`}
                    className="px-2.5 py-1 bg-[var(--background-elevated)] border border-[var(--border)] text-xs rounded-full text-[var(--text-secondary)] hover:border-[var(--primary)] hover:text-[var(--primary)] transition-colors"
                    data-testid={`biz-tag-${t.slug}`}
                  >
                    #{t.label}
                    {t.count > 0 && <span className="ml-1 text-[10px] opacity-60">({t.count})</span>}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {relatedContent.neighborhoods?.length > 0 && (
            <div className="mb-6" data-testid="biz-neighborhoods">
              <h3 className="text-sm font-semibold mb-2 text-[var(--text-secondary)]">
                {relatedContent.neighborhoods_scope === 'similares'
                  ? 'Bairros com negócios similares'
                  : 'Bairros com negócios'}
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {relatedContent.neighborhoods.map(n => (
                  <span key={n.nome} className="px-2.5 py-1 bg-[var(--background-elevated)] text-xs rounded-full text-[var(--text-secondary)]">
                    {n.nome} ({n.count})
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal de Reivindicação */}
      {showClaimModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Reivindicar este negócio</h3>
              <button
                onClick={() => {
                  setShowClaimModal(false);
                  setClaimStatus(null);
                  setClaimCode('');
                }}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!claimStatus ? (
              <>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  Para reivindicar este negócio como proprietário, enviaremos um código de verificação 
                  para o WhatsApp cadastrado no negócio.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>WhatsApp do negócio:</strong> {business?.whatsapp || 'Não informado'}
                  </p>
                </div>
                <button
                  onClick={handleSubmitClaim}
                  disabled={submittingClaim}
                  className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingClaim ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    'Enviar código de verificação'
                  )}
                </button>
              </>
            ) : claimStatus.status === 'DISPUTE' ? (
              <>
                {claimStatus.document_uploaded ? (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-green-800">
                        <strong>Documento enviado!</strong> Nossa equipe está analisando sua solicitação.
                        Você será notificado por email em até 3 dias úteis.
                      </p>
                    </div>
                    <button
                      onClick={() => setShowClaimModal(false)}
                      className="w-full px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-gray-50"
                    >
                      Entendido
                    </button>
                  </>
                ) : (
                  <>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-4">
                      <p className="text-sm text-yellow-800">
                        <strong>Atenção:</strong> Este negócio já possui um proprietário registrado.
                        Sua solicitação está em análise pela equipe de moderação.
                      </p>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)]">
                      Você será notificado por email sobre o resultado da análise.
                    </p>
                    <button
                      onClick={() => setShowClaimModal(false)}
                      className="w-full mt-4 px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-gray-50"
                    >
                      Entendido
                    </button>
                  </>
                )}
              </>
            ) : claimStatus.status === 'DOCUMENT_PENDING' || claimStatus.requires_document ? (
              <>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Documentação necessária:</strong> Como este negócio já possui um proprietário 
                    registrado, precisamos de um documento que comprove sua propriedade.
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Document Type */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Tipo de documento: <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={documentType}
                      onChange={(e) => setDocumentType(e.target.value)}
                      className="w-full px-4 py-2 border border-[var(--border)] rounded-lg"
                    >
                      <option value="CNPJ">Cartão CNPJ</option>
                      <option value="CONTRATO_SOCIAL">Contrato Social</option>
                      <option value="ALVARA">Alvará de Funcionamento</option>
                      <option value="OUTRO">Outro documento</option>
                    </select>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Documento (PDF ou imagem): <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={handleDocumentFileChange}
                      className="w-full px-4 py-2 border border-[var(--border)] rounded-lg"
                      data-testid="claim-document-input"
                    />
                    {documentFile && (
                      <p className="text-xs text-green-600 mt-1">
                        Arquivo selecionado: {documentFile.name}
                      </p>
                    )}
                    <p className="text-xs text-[var(--text-muted)] mt-1">
                      Formatos aceitos: PDF, JPG, PNG. Máximo: 10MB
                    </p>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Descrição (opcional):
                    </label>
                    <textarea
                      value={documentDescription}
                      onChange={(e) => setDocumentDescription(e.target.value)}
                      placeholder="Informações adicionais sobre o documento..."
                      className="w-full px-4 py-2 border border-[var(--border)] rounded-lg resize-none"
                      rows={2}
                    />
                  </div>

                  <button
                    onClick={handleUploadDocument}
                    disabled={!documentFile || submittingClaim}
                    className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)] disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submittingClaim ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      'Enviar documento'
                    )}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-[var(--text-secondary)] mb-4">
                  {claimStatus.message}
                </p>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">
                    Digite o código de verificação:
                  </label>
                  <input
                    type="text"
                    value={claimCode}
                    onChange={(e) => setClaimCode(e.target.value)}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full px-4 py-3 border border-[var(--border)] rounded-lg text-center text-2xl tracking-widest"
                    data-testid="claim-code-input"
                  />
                </div>
                <button
                  onClick={handleVerifyClaim}
                  disabled={claimCode.length !== 6 || submittingClaim}
                  className="w-full px-4 py-3 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary-dark)] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingClaim ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    'Verificar e reivindicar'
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Painel "Meu Negócio" Lite — abre pro dono logado direto da página do negócio. */}
      {showLiteEditor && business && (
        <MeuNegocioLiteModal
          business={business}
          onClose={() => setShowLiteEditor(false)}
          onSaved={(updated) => {
            setBusiness((prev) => ({ ...prev, ...updated }));
          }}
        />
      )}

      {/* Modal de login inline — abre em cima da página do negócio quando um
          usuário deslogado clica Avaliar/Comentar/Denunciar. Após login o
          modal fecha e o usuário permanece na página pra completar a ação. */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={() => setShowAuthModal(false)}
      />
    </div>
  );
}
